// src/pages/OfferDetail.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header  from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { edtTime } from "../utils/edt";
import axios from "axios";
import "../styles/offer-detail.css";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";

/* ─── Auth helpers (token in URL for browser-initiated downloads) ─── */
function readAuthToken(authObj) {
  if (authObj && authObj.token) return authObj.token;
  try { return localStorage.getItem("ipanel_token") || ""; } catch (e) { return ""; }
}
function appendToken(url, token) {
  if (!token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/* ─── Email category-heading detector (FROM LINES (...) etc.) ─── */
function isEmailHeading(line) {
  if (!line || typeof line !== "string") return false;
  const trimmed = line.trim();
  if (trimmed.length < 5)     return false;
  if (!trimmed.endsWith(":")) return false;
  const letters = trimmed.replace(/[^a-zA-Z]/g, "");
  if (letters.length < 3)     return false;
  const uppercase = trimmed.replace(/[^A-Z]/g, "");
  return (uppercase.length / letters.length) >= 0.7;
}

/* ─── HTML detect+clean for creative preview iframe ─── */
function detectAndCleanHtml(content) {
  if (!content || typeof content !== "string") return { isHtml: false, cleaned: null };
  const trimmed = content.trim();
  const firstAngle = trimmed.indexOf("<");
  if (firstAngle === -1) return { isHtml: false, cleaned: trimmed };
  const cleaned = firstAngle > 0 ? trimmed.substring(firstAngle) : trimmed;
  const lookahead = cleaned.substring(0, 500).toLowerCase();
  const isHtml = /<(html|body|table|!doctype|head|div|td|tr|p|span|img|a|h1|h2|h3|h4|h5|h6|meta|style|title|br|strong|em|b|i|u|font|center|hr|ul|ol|li|nav|section|article|figure|figcaption|small|sub|sup|mark|del|ins|q|blockquote|pre|code|button|input|form|label|select|option|textarea)[\s>/]/i.test(lookahead);
  return { isHtml, cleaned };
}

/* ─── HTML/text content prep for the Description tab ─── */
function prepHtmlContent(content) {
  if (!content || typeof content !== "string") return null;
  return content
    .replace(/\r\n/g, "\n")
    .replace(/\n/g, "<br>");
}

/* ─── Status Badge ─── */
function StatusBadge({ value }) {
  if (!value) return <span className="od-badge od-badge-grey">—</span>;
  const lower = String(value).toLowerCase();
  let cls = "od-badge-grey";
  if (lower === "approved" || lower === "active" || lower === "public") cls = "od-badge-green";
  else if (lower === "pending" || lower === "apply to run" || lower === "require_approval") cls = "od-badge-amber";
  else if (lower === "rejected" || lower === "private") cls = "od-badge-red";
  return (
    <span className={`od-badge ${cls}`}>
      <span className="od-badge-dot">•</span>
      {value}
    </span>
  );
}

/* ─── Help icon ─── */
function HelpIcon({ tooltip }) {
  return (
    <span className="od-help-icon" title={tooltip}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    </span>
  );
}

/* ─── Section Card ─── */
function Section({ title, defaultOpen = true, children, headerExtra }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="od-section">
      <div className="od-section-head" onClick={() => setOpen(!open)}>
        <div className="od-section-head-left">
          <span className={`od-section-arrow ${open ? "open" : ""}`}>▾</span>
          <span className="od-section-title">{title}</span>
        </div>
        {headerExtra && (
          <div className="od-section-head-right" onClick={e => e.stopPropagation()}>
            {headerExtra}
          </div>
        )}
      </div>
      {open && <div className="od-section-body">{children}</div>}
    </div>
  );
}

/* ─── Copy button ─── */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <button className="od-copy-btn" onClick={handleCopy} title="Copy to clipboard">
      {copied ? "✓ Copied" : (
        <>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

/* ─── Date helpers ─── */
function fmtDate(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "—";
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const yyyy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    const ss = String(dt.getSeconds()).padStart(2, "0");
    return `${mm}/${dd}/${yyyy}\n${hh}:${mi}:${ss} EDT`;
  } catch (e) { return "—"; }
}
function fmtPayout(offer) {
  if (!offer?.payout_type) return "—";
  if (offer.payout_type === "RevShare") {
    return offer.payout_percentage != null
      ? `RevShare ${parseFloat(offer.payout_percentage).toFixed(2)}%`
      : "RevShare —";
  }
  return offer.payout_amount != null
    ? `${offer.payout_type} $${parseFloat(offer.payout_amount).toFixed(2)}`
    : `${offer.payout_type} —`;
}
function fmtCurrency(c) {
  if (!c) return "—";
  if (c === "USD") return "$ US Dollar (USD)";
  if (c === "EUR") return "€ Euro (EUR)";
  if (c === "GBP") return "£ British Pound (GBP)";
  return c;
}

/* ─── Tracking URL builder (Phase 5.1, unchanged) ─── */
function buildTrackingUrl(baseUrl, sub1, sourceId, creativeId) {
  if (!baseUrl) return "";
  let url = baseUrl;
  if (sub1) {
    const enc = encodeURIComponent(sub1);
    if (/[?&]s1=(?![\w%])/.test(url)) {
      url = url.replace(/([?&]s1=)/, `$1${enc}`);
    } else {
      url += (url.includes("?") ? "&" : "?") + "sub1=" + enc;
    }
  }
  if (sourceId) {
    url += (url.includes("?") ? "&" : "?") + "source_id=" + encodeURIComponent(sourceId);
  }
  if (creativeId) {
    const idStr = String(creativeId);
    const cakePattern = new RegExp(`[?&]c=${idStr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:[&]|$)`);
    if (!cakePattern.test(url)) {
      url += (url.includes("?") ? "&" : "?") + "creative_id=" + encodeURIComponent(idStr);
    }
  }
  return url;
}

/* ═══════════════════════════════════════════════════════════════════
   Tracking & Asset Generator Modal
   ───────────────────────────────────────────────────────────────────
   Phase 5.1   — raw affiliate tracking link generator (top of modal)
   Phase 5.1b  — branded redirect deployer (bottom of modal)
   ═══════════════════════════════════════════════════════════════════ */
function TrackingLinksModal({ offer, onClose }) {
  const isCakeOffer = offer?.affiliate_type === "cake";

  const usableCreatives = useMemo(() => {
    return (offer?.creatives || []).filter(c =>
      c.creative_type !== "from_line" &&
      c.creative_type !== "subject_line" &&
      c.creative_type !== "zip_bundle" &&
      c.tracking_url
    );
  }, [offer]);

  const altUrls = useMemo(() => {
    return (offer?.tracking || []).filter(t =>
      t.tracking_type === "alt_url" && t.url_external_id
    );
  }, [offer]);

  /* Unsub URL pulled from offer.tracking[] — used by the new branded
     redirect "Fetch from Offer" button. Looks for a dedicated unsub
     entry first, falls back to optizmo. */
  const offerUnsubUrl = useMemo(() => {
    const list = offer?.tracking || [];
    const unsub = list.find(t => t.tracking_type === "unsub" && t.tracking_url);
    if (unsub) return unsub.tracking_url;
    const optizmo = list.find(t => t.tracking_type === "optizmo" && t.tracking_url);
    if (optizmo) return optizmo.tracking_url;
    return "";
  }, [offer]);

  const primaryTrackingUrl = useMemo(() => {
    if (usableCreatives.length === 0) return offer?.preview_url || "";
    const linkCreative = usableCreatives.find(c => c.creative_type === "link");
    if (linkCreative?.tracking_url) return linkCreative.tracking_url;
    return usableCreatives[0].tracking_url;
  }, [usableCreatives, offer]);

  const [selectedCreativeId, setSelectedCreativeId] = useState("");
  const [urlMode,   setUrlMode]   = useState("default");
  const [sourceId,  setSourceId]  = useState("");
  const [sub1,      setSub1]      = useState("");
  const [paramsOpen, setParamsOpen] = useState(true);

  const baseUrl = useMemo(() => {
    if (urlMode.startsWith("alt_")) {
      const idx = parseInt(urlMode.slice(4));
      if (!isNaN(idx) && altUrls[idx] && altUrls[idx].url_external_id) {
        const cleanPrimary = primaryTrackingUrl.split("?")[0];
        return `${cleanPrimary}?uid=${encodeURIComponent(altUrls[idx].url_external_id)}`;
      }
    }
    if (selectedCreativeId) {
      const c = usableCreatives.find(x => String(x.id) === String(selectedCreativeId));
      if (c?.tracking_url) return c.tracking_url;
    }
    return primaryTrackingUrl;
  }, [selectedCreativeId, urlMode, usableCreatives, altUrls, primaryTrackingUrl]);

  const creativeIdForUrl = useMemo(() => {
    if (!selectedCreativeId) return "";
    const c = usableCreatives.find(x => String(x.id) === String(selectedCreativeId));
    if (!c) return "";
    return c.creative_external_id || c.id || "";
  }, [selectedCreativeId, usableCreatives]);

  const willAppendCreativeId = useMemo(() => {
    if (!creativeIdForUrl) return false;
    const idStr = String(creativeIdForUrl);
    const cakePattern = new RegExp(`[?&]c=${idStr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:[&]|$)`);
    return !cakePattern.test(baseUrl);
  }, [creativeIdForUrl, baseUrl]);

  const generatedUrl = useMemo(
    () => buildTrackingUrl(baseUrl, sub1, sourceId, creativeIdForUrl),
    [baseUrl, sub1, sourceId, creativeIdForUrl]
  );

  /* ── Phase 5.1b — Branded Redirect state ─────────────────────── */
  const [redirectDomain, setRedirectDomain] = useState("");
  const [hostingType,    setHostingType]    = useState("cpanel");
  const [clickPath,      setClickPath]      = useState("");
  const [unsubPath,      setUnsubPath]      = useState("");
  const [unsubUrl,       setUnsubUrl]       = useState("");
  const [deploying,      setDeploying]      = useState(false);
  const [deployResult,   setDeployResult]   = useState(null);
  const [deployError,    setDeployError]    = useState(null);

  const fetchUnsubFromOffer = () => {
    if (!offerUnsubUrl) {
      setDeployError({
        message: "This offer has no unsubscribe link configured. Try Refresh on the offer page, or paste the unsub URL manually.",
        code: "NO_UNSUB",
      });
      return;
    }
    setUnsubUrl(offerUnsubUrl);
    setDeployError(null);
  };

  const canCreateRedirect =
    !!generatedUrl &&
    !!redirectDomain.trim() &&
    !!clickPath.trim() &&
    !!unsubPath.trim() &&
    !!unsubUrl.trim() &&
    !deploying;

  /* ── Client-side validation (mirrors backend validateRedirectPath) ─
     Catches bad input BEFORE making the HTTP request. The backend
     repeats these same checks as defense-in-depth — never trust
     client validation alone. */
  const validatePathClient = (raw, fieldName) => {
    if (!raw || typeof raw !== "string") return `${fieldName} is required`;
    const p = raw.trim();
    if (p.length === 0)         return `${fieldName} cannot be empty`;
    if (!p.startsWith("/"))     return `${fieldName} must start with /`;
    if (p === "/")              return `${fieldName} must have at least one folder name after /`;
    if (p.endsWith("/"))        return `${fieldName} must not end with /`;
    if (p.includes("//"))       return `${fieldName} contains empty segments (//)`;
    if (p.includes(".."))       return `${fieldName} cannot contain ..`;
    if (!/^(\/[a-zA-Z0-9_-]+)+$/.test(p)) {
      return `${fieldName} contains invalid characters. Only letters, numbers, hyphens, and underscores are allowed in folder names.`;
    }
    return null;
  };

  const validateDomainClient = (raw) => {
    if (!raw || typeof raw !== "string") return "Domain is required";
    const d = raw.trim().toLowerCase();
    if (d.length === 0)     return "Domain cannot be empty";
    if (d.includes(" "))    return "Domain cannot contain spaces";
    if (d.includes("://"))  return "Domain should not include http:// or https://";
    if (d.includes("/"))    return "Domain should not contain /";
    if (!/^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/.test(d)) {
      return "Invalid domain format. Example: example.com";
    }
    return null;
  };

  /* Returns null when all clean, else { domain, click_path, unsub_path } */
  const validateAllRedirectFields = () => {
    const errors = {};
    const dErr = validateDomainClient(redirectDomain);
    if (dErr) errors.domain = dErr;
    const cErr = validatePathClient(clickPath, "Click Path");
    if (cErr) errors.click_path = cErr;
    const uErr = validatePathClient(unsubPath, "Unsub Path");
    if (uErr) errors.unsub_path = uErr;
    if (!unsubUrl || !unsubUrl.trim()) {
      errors.unsub_destination_url = "Unsub Destination URL is required";
    }
    if (!cErr && !uErr && clickPath.trim() === unsubPath.trim()) {
      errors.unsub_path = "Must be different from Click Path";
    }
    return Object.keys(errors).length ? errors : null;
  };

  const handleCreateRedirect = async () => {
    setDeployError(null);
    setDeployResult(null);

    // Client-side gate — show validation popup if anything's off
    const validationErrors = validateAllRedirectFields();
    if (validationErrors) {
      setDeployError({
        code: "VALIDATION_ERROR",
        message: "One or more fields are invalid",
        fieldErrors: validationErrors,
      });
      return;
    }

    setDeploying(true);
    try {
      const res = await axios.post(`${API}/tracking-links/deploy`, {
        offer_id:              offer?.id || null,
        domain:                redirectDomain.trim(),
        hosting_type:          hostingType,
        click_path:            clickPath.trim(),
        click_destination_url: generatedUrl,
        unsub_path:            unsubPath.trim(),
        unsub_destination_url: unsubUrl.trim(),
      });
      setDeployResult({
        click_link: res.data.click_link,
        unsub_link: res.data.unsub_link,
        link_id:    res.data.link_id,
      });
    } catch (err) {
      const data = err.response?.data || {};
      setDeployError({
        message:     data.message || err.message || "Deploy failed",
        code:        data.code,
        fieldErrors: data.fieldErrors || null,
      });
    } finally {
      setDeploying(false);
    }
  };

  const resetRedirectForm = () => {
    setDeployResult(null);
    setDeployError(null);
  };

  return (
    <div className="od-modal-backdrop" onClick={onClose}>
      <div className="od-modal od-modal-large od-tlm" onClick={e => e.stopPropagation()}>
        <div className="od-modal-head">
          <h2>Tracking & Asset Generator</h2>
          <button className="od-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="od-modal-body od-tlm-body">
          {/* LEFT: form */}
          <div className="od-tlm-form">
            <div className="od-tlm-field-group">
              <label className="od-tlm-label">Bundle Type *</label>
              <div className="od-tlm-segmented">
                <button className="od-tlm-seg-btn od-tlm-seg-active" type="button">Single Offer</button>
                <button className="od-tlm-seg-btn od-tlm-seg-disabled" type="button" disabled title="Coming soon">Multiple Offers</button>
                <button className="od-tlm-seg-btn od-tlm-seg-disabled" type="button" disabled title="Coming soon">All Offers</button>
              </div>
            </div>

            <div className="od-tlm-field-group">
              <label className="od-tlm-label">Offer *</label>
              <div className="od-tlm-readonly">
                ({offer?.offer_external_id}) {offer?.offer_name}
              </div>
            </div>

            <div className="od-tlm-field-group">
              <label className="od-tlm-label">Type</label>
              <div className="od-tlm-segmented">
                <button className="od-tlm-seg-btn od-tlm-seg-active" type="button">Click</button>
              </div>
            </div>

            <div className="od-tlm-field-group">
              <label className="od-tlm-label">Creative (Optional)</label>
              <select
                className="od-tlm-select"
                value={selectedCreativeId}
                onChange={e => setSelectedCreativeId(e.target.value)}>
                <option value="">Select Creative (Optional)…</option>
                {usableCreatives.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.creative_external_id ? `(${c.creative_external_id}) ` : ""}
                    {c.creative_name || `Creative ${c.id}`}
                  </option>
                ))}
              </select>
              {selectedCreativeId && willAppendCreativeId && (
                <div className="od-tlm-hint">
                  ✓ creative_id={creativeIdForUrl} will be appended to URL
                </div>
              )}
              {selectedCreativeId && !willAppendCreativeId && (
                <div className="od-tlm-hint">
                  ✓ Creative #{creativeIdForUrl} is already encoded in the URL (c={creativeIdForUrl})
                </div>
              )}
            </div>

            <div className="od-tlm-field-group">
              <label className="od-tlm-label">URL</label>
              <select
                className="od-tlm-select"
                value={urlMode}
                onChange={e => setUrlMode(e.target.value)}>
                <option value="default">Default Landing Page URL</option>
                {altUrls.map((u, idx) => (
                  <option key={idx} value={`alt_${idx}`}>
                    {u.url_external_id ? `(${u.url_external_id}) ` : ""}
                    {u.tracking_label || `Alt URL ${idx + 1}`}
                  </option>
                ))}
              </select>
              {altUrls.length === 0 && (
                <div className="od-tlm-hint">
                  Only the default URL is configured for this offer.
                </div>
              )}
              {urlMode.startsWith("alt_") && (() => {
                const idx = parseInt(urlMode.slice(4));
                const alt = altUrls[idx];
                return alt ? (
                  <div className="od-tlm-hint">
                    ✓ uid={alt.url_external_id} will route to: {alt.tracking_url}
                  </div>
                ) : null;
              })()}
            </div>

            <div className="od-tlm-collapse">
              <button
                type="button"
                className="od-tlm-collapse-head"
                onClick={() => setParamsOpen(!paramsOpen)}>
                <span className={`od-tlm-collapse-arrow ${paramsOpen ? "open" : ""}`}>▸</span>
                <span>Additional Parameters</span>
              </button>
              {paramsOpen && (
                <div className="od-tlm-collapse-body">
                  {!isCakeOffer && (
                    <div className="od-tlm-field-group">
                      <label className="od-tlm-label-sm">source_id</label>
                      <input
                        type="text"
                        className="od-tlm-input"
                        value={sourceId}
                        onChange={e => setSourceId(e.target.value)}
                        placeholder=""
                      />
                    </div>
                  )}
                  <div className="od-tlm-field-group">
                    <label className="od-tlm-label-sm">sub1</label>
                    <input
                      type="text"
                      className="od-tlm-input"
                      value={sub1}
                      onChange={e => setSub1(e.target.value)}
                      placeholder=""
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: tracking link panel + branded redirect panel */}
          <div className="od-tlm-result">
            {/* ── Existing: raw affiliate tracking link ─────────── */}
            <div className="od-tlm-result-head">
              <h3 className="od-tlm-result-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     style={{ marginRight: "6px", verticalAlign: "middle" }}>
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                Tracking Link(s)
              </h3>
            </div>

            <div className="od-tlm-url-box">
              {generatedUrl ? (
                <code className="od-tlm-url-text">{generatedUrl}</code>
              ) : (
                <span className="od-tlm-url-empty">No tracking URL available for this offer.</span>
              )}
            </div>

            <div className="od-tlm-actions">
              <CopyButton text={generatedUrl}/>
            </div>

            {/* ── Phase 5.1b: Branded Redirect ─────────────────────
                COMPACT layout: 2-column grid for parallel fields,
                inline doc-root hint, slim segmented control. */}
            <div className="od-tlm-redirect od-tlm-redirect-compact">
              <div className="od-tlm-redirect-head">
                <h3 className="od-tlm-redirect-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       style={{ verticalAlign: "middle", marginRight: "6px" }}>
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  Branded Redirect <span className="od-tlm-redirect-sub">deploy to your hosting</span>
                </h3>
              </div>

              {!deployResult && (
                <>
                  {/* Row 1: Domain (grow) + Hosting Type (fixed) */}
                  <div className="od-tlm-grid-row">
                    <div className="od-tlm-grid-col od-tlm-grid-grow">
                      <label className="od-tlm-label-tiny">Domain</label>
                      <input
                        type="text"
                        className={`od-tlm-input od-tlm-input-sm ${
                          deployError?.fieldErrors?.domain ? "od-tlm-input-error" : ""
                        }`}
                        value={redirectDomain}
                        onChange={e => {
                          setRedirectDomain(e.target.value);
                          // clear the field's error on edit
                          if (deployError?.fieldErrors?.domain) {
                            setDeployError(prev => prev ? {
                              ...prev,
                              fieldErrors: { ...prev.fieldErrors, domain: undefined },
                            } : null);
                          }
                        }}
                        placeholder="example.com"
                        disabled={deploying}
                      />
                      {deployError?.fieldErrors?.domain && (
                        <div className="od-tlm-field-error">{deployError.fieldErrors.domain}</div>
                      )}
                    </div>
                    <div className="od-tlm-grid-col od-tlm-grid-fixed">
                      <label className="od-tlm-label-tiny">Hosting</label>
                      <div className="od-tlm-segmented od-tlm-segmented-sm">
                        <label className={`od-tlm-seg-btn-sm ${hostingType === "cpanel" ? "od-tlm-seg-active" : ""}`}>
                          <input
                            type="radio"
                            name="hosting_type"
                            value="cpanel"
                            checked={hostingType === "cpanel"}
                            onChange={() => setHostingType("cpanel")}
                            disabled={deploying}
                          />
                          cPanel
                        </label>
                        <label className={`od-tlm-seg-btn-sm ${hostingType === "plesk" ? "od-tlm-seg-active" : ""}`}>
                          <input
                            type="radio"
                            name="hosting_type"
                            value="plesk"
                            checked={hostingType === "plesk"}
                            onChange={() => setHostingType("plesk")}
                            disabled={deploying}
                          />
                          Plesk
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Doc root hint — slim inline strip */}
                  <div className="od-tlm-doc-root">
                    <span className="od-tlm-doc-root-icon">📁</span>
                    <code className="od-tlm-doc-root-code">
                      {hostingType === "cpanel"
                        ? `/home/${redirectDomain ? redirectDomain.split(".")[0] : "<user>"}/public_html/`
                        : `/var/www/vhosts/${redirectDomain || "<domain>"}/httpdocs/`}
                    </code>
                  </div>

                  {/* Click Path — full width row */}
                  <div className="od-tlm-field-group od-tlm-field-tight">
                    <label className="od-tlm-label-tiny">Click Path</label>
                    <input
                      type="text"
                      className={`od-tlm-input od-tlm-input-sm ${
                        deployError?.fieldErrors?.click_path ? "od-tlm-input-error" : ""
                      }`}
                      value={clickPath}
                      onChange={e => {
                        setClickPath(e.target.value);
                        if (deployError?.fieldErrors?.click_path) {
                          setDeployError(prev => prev ? {
                            ...prev,
                            fieldErrors: { ...prev.fieldErrors, click_path: undefined },
                          } : null);
                        }
                      }}
                      placeholder="/promo/blast1/click"
                      disabled={deploying}
                    />
                    {deployError?.fieldErrors?.click_path && (
                      <div className="od-tlm-field-error">{deployError.fieldErrors.click_path}</div>
                    )}
                  </div>

                  {/* Unsub Path — full width row */}
                  <div className="od-tlm-field-group od-tlm-field-tight">
                    <label className="od-tlm-label-tiny">Unsub Path</label>
                    <input
                      type="text"
                      className={`od-tlm-input od-tlm-input-sm ${
                        deployError?.fieldErrors?.unsub_path ? "od-tlm-input-error" : ""
                      }`}
                      value={unsubPath}
                      onChange={e => {
                        setUnsubPath(e.target.value);
                        if (deployError?.fieldErrors?.unsub_path) {
                          setDeployError(prev => prev ? {
                            ...prev,
                            fieldErrors: { ...prev.fieldErrors, unsub_path: undefined },
                          } : null);
                        }
                      }}
                      placeholder="/promo/blast1/unsub"
                      disabled={deploying}
                    />
                    {deployError?.fieldErrors?.unsub_path && (
                      <div className="od-tlm-field-error">{deployError.fieldErrors.unsub_path}</div>
                    )}
                  </div>

                  {/* Unsub Destination URL — label + fetch button in one row */}
                  <div className="od-tlm-field-group od-tlm-field-tight">
                    <div className="od-tlm-label-row">
                      <label className="od-tlm-label-tiny">Unsub Destination URL</label>
                      <button
                        type="button"
                        className="od-tlm-link-btn-sm"
                        onClick={fetchUnsubFromOffer}
                        disabled={deploying}>
                        ↓ Fetch from Offer
                      </button>
                    </div>
                    <input
                      type="text"
                      className="od-tlm-input od-tlm-input-sm"
                      value={unsubUrl}
                      onChange={e => setUnsubUrl(e.target.value)}
                      placeholder="https://...unsubscribe..."
                      disabled={deploying}
                    />
                  </div>

                  {/* Inline error for non-popup error codes */}
                  {deployError &&
                   deployError.code !== "PATH_EXISTS_HOSTING" &&
                   deployError.code !== "PATH_EXISTS_DB" &&
                   deployError.code !== "VALIDATION_ERROR" && (
                    <div className="od-tlm-error-inline">
                      ⚠ {deployError.message}
                    </div>
                  )}

                  {/* Action button — right aligned, primary style */}
                  <div className="od-tlm-actions od-tlm-actions-end">
                    <button
                      className="od-tlm-deploy-btn"
                      onClick={handleCreateRedirect}
                      disabled={!canCreateRedirect}>
                      {deploying ? (
                        <>
                          <span className="od-tlm-spinner"/>
                          Deploying...
                        </>
                      ) : "Create Redirects"}
                    </button>
                  </div>
                </>
              )}

              {/* Success state */}
              {deployResult && (
                <div className="od-tlm-redirect-success">
                  <div className="od-tlm-success-banner">
                    ✓ Redirects deployed successfully on {redirectDomain}
                  </div>

                  <div className="od-tlm-result-row">
                    <label className="od-tlm-label-sm">Click Link:</label>
                    <div className="od-tlm-url-box">
                      <code className="od-tlm-url-text">{deployResult.click_link}</code>
                    </div>
                    <div className="od-tlm-actions"><CopyButton text={deployResult.click_link}/></div>
                  </div>

                  <div className="od-tlm-result-row" style={{ marginTop: "12px" }}>
                    <label className="od-tlm-label-sm">Unsub Link:</label>
                    <div className="od-tlm-url-box">
                      <code className="od-tlm-url-text">{deployResult.unsub_link}</code>
                    </div>
                    <div className="od-tlm-actions"><CopyButton text={deployResult.unsub_link}/></div>
                  </div>

                  <div className="od-tlm-actions" style={{ marginTop: "16px" }}>
                    <button className="od-tlm-link-btn" onClick={resetRedirectForm}>
                      Create another
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* VALIDATION popup — centered modal-on-top-of-modal */}
      {deployError && deployError.code === "VALIDATION_ERROR" && deployError.fieldErrors && (
        <div
          className="od-modal-backdrop"
          style={{ zIndex: 1100 }}
          onClick={() => setDeployError(null)}>
          <div
            className="od-modal od-validation-modal"
            onClick={e => e.stopPropagation()}>
            <div className="od-validation-head">
              <div className="od-validation-icon">⚠</div>
              <h3 className="od-validation-title">Invalid input</h3>
              <p className="od-validation-sub">
                Please fix the following before deploying:
              </p>
            </div>

            <ul className="od-validation-list">
              {Object.entries(deployError.fieldErrors)
                .filter(([_, msg]) => !!msg)
                .map(([field, msg]) => (
                  <li key={field} className="od-validation-item">
                    <strong className="od-validation-field">
                      {{
                        domain:                "Domain",
                        click_path:            "Click Path",
                        unsub_path:            "Unsub Path",
                        unsub_destination_url: "Unsub Destination URL",
                        hosting_type:          "Hosting Type",
                      }[field] || field}:
                    </strong>{" "}
                    <span>{msg}</span>
                  </li>
                ))}
            </ul>

            <div className="od-validation-format-box">
              <div className="od-validation-format-title">Required path format:</div>
              <code className="od-validation-format-code">
                /Directory/Subdirectory1/Subdirectory2
              </code>
              <div className="od-validation-rules">
                <div className="od-validation-rule">
                  <span className="od-validation-check">✓</span>
                  Letters, numbers, hyphens (-) and underscores (_) only
                </div>
                <div className="od-validation-rule">
                  <span className="od-validation-cross">✗</span>
                  No spaces, dots, or special characters
                </div>
                <div className="od-validation-rule">
                  <span className="od-validation-cross">✗</span>
                  No trailing slash, no .. (path traversal)
                </div>
              </div>
              <div className="od-validation-examples">
                <div className="od-validation-example-row">
                  <span className="od-validation-check">✓</span>
                  <code>/promo/blast1/click</code>
                </div>
                <div className="od-validation-example-row">
                  <span className="od-validation-check">✓</span>
                  <code>/campaigns/2026-Q2/email-1/click</code>
                </div>
                <div className="od-validation-example-row">
                  <span className="od-validation-cross">✗</span>
                  <code>/promo/click; rm</code>
                  <span className="od-validation-example-note">contains ; and space</span>
                </div>
                <div className="od-validation-example-row">
                  <span className="od-validation-cross">✗</span>
                  <code>promo/click</code>
                  <span className="od-validation-example-note">missing leading /</span>
                </div>
              </div>
            </div>

            <div className="od-validation-actions">
              <button
                className="od-tlm-deploy-btn"
                onClick={() => setDeployError(null)}>
                OK, I'll fix it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PATH-EXISTS popup — centered modal-on-top-of-modal */}
      {deployError &&
       (deployError.code === "PATH_EXISTS_HOSTING" || deployError.code === "PATH_EXISTS_DB") && (
        <div
          className="od-modal-backdrop"
          style={{ zIndex: 1100 }}
          onClick={() => setDeployError(null)}>
          <div
            className="od-modal"
            style={{ maxWidth: "440px", padding: "24px" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "40px", marginBottom: "12px" }}>⚠</div>
              <h3 style={{ margin: "0 0 12px 0", color: "#b45309" }}>Path Already Exists</h3>
              <p style={{ margin: "0 0 20px 0", color: "#525252", lineHeight: 1.5 }}>
                {deployError.message}
              </p>
              <button
                className="od-tracking-btn"
                onClick={() => setDeployError(null)}
                style={{ minWidth: "120px" }}>
                OK, I'll change it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Creative Preview Modal ─── */
function CreativePreviewModal({ creative, affId, offerId, token, onClose }) {
  const [activeTab, setActiveTab] = useState("preview");

  const { isHtml: hasHtmlContent, cleaned: cleanedContent } = detectAndCleanHtml(creative.creative_content);
  const url        = creative.creative_url;
  const isImageUrl = url && /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url);
  const hasUrl     = !!url && !isImageUrl;
  const isPlainText= creative.creative_content && !hasHtmlContent;
  const showHtmlTab= hasHtmlContent;

  const handleDownload = () => {
    const dlUrl = appendToken(
      `${API}/offers/${affId}/${encodeURIComponent(offerId)}/creative/${creative.id}/download`,
      token
    );
    window.open(dlUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="od-modal-backdrop" onClick={onClose}>
      <div className="od-modal od-modal-large" onClick={e => e.stopPropagation()}>
        <div className="od-modal-head">
          <h2 className="od-cprev-title">{creative.creative_name || `Creative #${creative.creative_external_id || creative.id}`}</h2>
          <button className="od-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="od-cprev-tabs">
          <button
            className={`od-cprev-tab ${activeTab === "preview" ? "od-cprev-tab-active" : ""}`}
            onClick={() => setActiveTab("preview")}>
            PREVIEW
          </button>
          {showHtmlTab && (
            <button
              className={`od-cprev-tab ${activeTab === "html" ? "od-cprev-tab-active" : ""}`}
              onClick={() => setActiveTab("html")}>
              HTML
            </button>
          )}
          <div className="od-cprev-tab-spacer"/>
          <button className="od-cprev-download-btn" onClick={handleDownload}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download
          </button>
        </div>

        <div className="od-modal-body od-cprev-body">
          {activeTab === "preview" && (
            <>
              {hasHtmlContent && (
                <iframe
                  className="od-cprev-iframe"
                  srcDoc={cleanedContent}
                  sandbox="allow-same-origin allow-popups"
                  title="Creative Preview"
                />
              )}
              {!hasHtmlContent && isImageUrl && (
                <div className="od-cprev-image-wrap">
                  <img src={url} alt={creative.creative_name || ""} className="od-cprev-image"/>
                </div>
              )}
              {!hasHtmlContent && !isImageUrl && hasUrl && (
                <iframe
                  className="od-cprev-iframe"
                  src={url}
                  sandbox="allow-same-origin allow-scripts allow-popups"
                  title="Creative Preview"
                />
              )}
              {!hasHtmlContent && !hasUrl && isPlainText && (
                <pre className="od-cprev-text">{creative.creative_content}</pre>
              )}
              {!hasHtmlContent && !hasUrl && !isPlainText && (
                <div className="od-cprev-placeholder">
                  <div className="od-cprev-ph-icon">📄</div>
                  <h3>{(creative.creative_type || "Creative").toUpperCase().replace(/_/g, " ")}</h3>
                  <p>No preview content available. Try Refresh on the page header to re-fetch from affiliate, or use Download.</p>
                  <button className="od-cprev-cta" onClick={handleDownload}>Download Original</button>
                </div>
              )}
            </>
          )}

          {activeTab === "html" && hasHtmlContent && (
            <div className="od-cprev-html-wrap">
              <div className="od-cprev-html-actions">
                <CopyButton text={cleanedContent}/>
              </div>
              <pre className="od-cprev-html-code"><code>{cleanedContent}</code></pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Email lines block ─── */
function EmailLinesBlock({ items }) {
  const allLines = [];
  items.forEach((item, itemIdx) => {
    const content = item.creative_content || "";
    const lines = content.split("\n");
    lines.forEach((line, lineIdx) => {
      allLines.push({
        key: `${itemIdx}-${lineIdx}`,
        text: line,
        isHeading: isEmailHeading(line),
        isEmpty: line.trim() === "",
      });
    });
  });

  return (
    <div className="od-email-textarea od-email-textarea-tall">
      {allLines.map((l) => {
        if (l.isEmpty)   return <div key={l.key} className="od-email-line-spacer"/>;
        if (l.isHeading) return <div key={l.key} className="od-email-line od-email-line-heading">{l.text}</div>;
        return <div key={l.key} className="od-email-line">{l.text}</div>;
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════ */
export default function OfferDetail() {
  const { affId, offerId } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();

  const token = readAuthToken(auth);

  const [offer,           setOffer]           = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");
  const [creativeSearch,  setCreativeSearch]  = useState("");
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [previewCreative, setPreviewCreative] = useState(null);
  const [generalTab,      setGeneralTab]      = useState("basis");
  const [refreshing,      setRefreshing]      = useState(false);

  const fetchOffer = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/offers/${affId}/${encodeURIComponent(offerId)}`);
      if (!res.data?.offer) {
        setError("Offer not found.");
        return;
      }
      setOffer({
        ...res.data.offer,
        creatives: res.data.creatives || [],
        tracking:  res.data.tracking  || [],
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load offer details.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [affId, offerId]);

  useEffect(() => { fetchOffer(); }, [fetchOffer]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/offers/${affId}/${encodeURIComponent(offerId)}/refresh`);
      await fetchOffer(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to refresh from affiliate.");
    } finally {
      setRefreshing(false);
    }
  };

  const headerSubtitle = offer?.details_fetched_at
    ? `Data synced from affiliate API at ${edtTime(new Date(offer.details_fetched_at))} EDT`
    : null;

  if (loading) {
    return (
      <div className="layout">
        <Sidebar active="offers"/>
        <div className="layout-main">
          <Header title="Offer Details"/>
          <div className="content">
            <div className="od-loading-card">
              <div className="od-spinner"/>
              <span>Loading offer details...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="layout">
        <Sidebar active="offers"/>
        <div className="layout-main">
          <Header title="Offer Details"/>
          <div className="content">
            <div className="od-error-card">
              <div className="od-error-icon">⚠</div>
              <h3>{error || "Offer not found"}</h3>
              <button className="od-back-btn" onClick={() => navigate("/offers")}>← Back to Offers</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const creatives    = offer.creatives || [];
  const fromLines    = creatives.filter(c => c.creative_type === "from_line");
  const subjectLines = creatives.filter(c => c.creative_type === "subject_line");

  const isZipBundle = (c) => {
    const type = (c.creative_type || "").toLowerCase();
    const name = (c.creative_name || "").toLowerCase();
    return type === "zip_bundle" || type === "zip" ||
           name.includes("all creatives") || name.includes("(zip)");
  };

  const zipBundles = creatives.filter(c =>
    c.creative_type !== "from_line" && c.creative_type !== "subject_line" && isZipBundle(c)
  );
  const realCreatives = creatives.filter(c =>
    c.creative_type !== "from_line" && c.creative_type !== "subject_line" && !isZipBundle(c)
  );

  const filteredCreatives = realCreatives.filter(c => {
    if (!creativeSearch.trim()) return true;
    const q = creativeSearch.toLowerCase();
    return (c.creative_name || "").toLowerCase().includes(q)
        || (c.creative_external_id || "").toLowerCase().includes(q)
        || (c.creative_type || "").toLowerCase().includes(q);
  });

  const tracking = offer.tracking || [];
  const optizmo  = tracking.find(t => t.tracking_type === "optizmo");
  const unsub    = tracking.find(t => t.tracking_type === "unsub");
  const suppression = tracking.find(t => t.tracking_type === "suppression");

  const fromLinesText    = fromLines.map(c => c.creative_content).filter(Boolean).join("\n");
  const subjectLinesText = subjectLines.map(c => c.creative_content).filter(Boolean).join("\n");

  const handleDownload = (creative) => {
    const url = appendToken(
      `${API}/offers/${affId}/${encodeURIComponent(offerId)}/creative/${creative.id}/download`,
      token
    );
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadAll = () => {
    if (zipBundles.length > 0) {
      handleDownload(zipBundles[0]);
    } else {
      const url = appendToken(
        `${API}/offers/${affId}/${encodeURIComponent(offerId)}/creatives/download-all`,
        token
      );
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const typeLabel = (t) => {
    const map = {
      "html_email": "HTML", "html": "HTML",
      "banner_image": "Image", "image": "Image",
      "text": "Text", "zip_bundle": "ZIP", "zip": "ZIP",
      "video": "Video", "email": "Email", "email_template": "Email",
      "link": "Link",
    };
    return map[t] || (t ? t.toUpperCase() : "—");
  };

  const deriveChannels = () => {
    if (offer.allowed_media_types) return offer.allowed_media_types;
    const types = new Set();
    if (creatives.some(c => c.creative_type === "html_email" || c.creative_type === "email" ||
                            c.creative_type === "from_line"  || c.creative_type === "subject_line")) {
      types.add("Email");
    }
    if (creatives.some(c => c.creative_type === "banner_image" || c.creative_type === "image")) types.add("Display");
    if (creatives.some(c => c.creative_type === "video")) types.add("Video");
    return types.size > 0 ? Array.from(types).join(", ") : "—";
  };

  const hasOptizmo = !!(optizmo?.optizmo_mailer_key || optizmo?.tracking_url || unsub?.tracking_url || suppression?.tracking_url);
  const hasEmail   = fromLines.length > 0 || subjectLines.length > 0;

  const descHtml         = prepHtmlContent(offer.description);
  const restrictionsHtml = prepHtmlContent(offer.restrictions);
  const termsHtml        = prepHtmlContent(offer.terms_and_conditions);
  const hasAnyDescContent = !!(descHtml || restrictionsHtml || termsHtml);

  return (
    <div className="layout">
      <Sidebar active="offers"/>
      <div className="layout-main">
        <Header title="Offer Details" subtitle={headerSubtitle}/>

        <div className="content">
          <div className="od-page-header">
            <div className="od-page-title-row">
              <h1 className="od-page-title">
                Offer Details: <span className="od-page-title-name">{offer.offer_name}</span>
              </h1>
              <div className="od-header-actions">
                <button
                  className="od-refresh-btn"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Force re-fetch latest data from affiliate API">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                       style={{ animation: refreshing ? "od-spin 1s linear infinite" : "none" }}>
                    <polyline points="23 4 23 10 17 10"/>
                    <polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
                <button className="od-tracking-btn" onClick={() => setShowTrackingModal(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  Offer Tracking Links
                </button>
              </div>
            </div>
          </div>

          <div className="od-2col-grid">
            <div className="od-col-left">
              <Section title="General">
                <div className="od-tabs">
                  <div
                    className={`od-tab ${generalTab === "basis" ? "od-tab-active" : ""}`}
                    onClick={() => setGeneralTab("basis")}>BASIS</div>
                  <div
                    className={`od-tab ${generalTab === "details" ? "od-tab-active" : ""}`}
                    onClick={() => setGeneralTab("details")}>DETAILS</div>
                  <div
                    className={`od-tab ${generalTab === "description" ? "od-tab-active" : ""}`}
                    onClick={() => setGeneralTab("description")}>DESCRIPTION</div>
                </div>

                {generalTab === "basis" && (
                  <div className="od-tab-content">
                    <div className="od-general-grid">
                      <div className="od-general-left">
                        <div className="od-field"><label>ID</label><div className="od-value">{offer.offer_external_id || "—"}</div></div>
                        <div className="od-field"><label>Name</label><div className="od-value">{offer.offer_name || "—"}</div></div>
                        <div className="od-field"><label>Category</label><div className="od-value">{offer.category_name || "—"}</div></div>
                        <div className="od-field"><label>Currency</label><div className="od-value">{fmtCurrency(offer.currency)}</div></div>
                        <div className="od-field"><label>Payout</label><div className="od-value od-value-payout">{fmtPayout(offer)}</div></div>
                        <div className="od-field"><label>Blocked Variables</label><div className="od-value">{offer.blocked_variables || "N/A"}</div></div>
                      </div>

                      <div className="od-general-right">
                        {offer.thumbnail_url && (
                          <div className="od-logo-wrap">
                            <img src={offer.thumbnail_url} alt="" className="od-logo"/>
                          </div>
                        )}
                        <div className="od-field"><label>Application Status</label><div className="od-value"><StatusBadge value={offer.status}/></div></div>
                        <div className="od-field"><label>Application</label><div className="od-value od-value-date">{fmtDate(offer.date_added)}</div></div>
                        <div className="od-field"><label>Modified</label><div className="od-value od-value-date">{fmtDate(offer.last_seen_at || offer.details_fetched_at)}</div></div>
                        <div className="od-field"><label>Created</label><div className="od-value od-value-date">{fmtDate(offer.date_added)}</div></div>
                        <div className="od-field"><label>Affiliate / Network</label><div className="od-value">{offer.affiliate_name || "—"}</div></div>
                      </div>
                    </div>
                  </div>
                )}

                {generalTab === "details" && (
                  <div className="od-tab-content">
                    <div className="od-general-grid">
                      <div className="od-general-left">
                        <div className="od-field">
                          <div className="od-field-label-row">
                            <label>App Identifier</label>
                            <HelpIcon tooltip="Mobile app identifier for app-install offers (iOS/Android). Not applicable for web/email offers."/>
                          </div>
                          <div className="od-value">{offer.app_identifier || "—"}</div>
                        </div>

                        <div className="od-field">
                          <label>Preview URL</label>
                          <div className="od-value od-value-link">
                            {offer.preview_url ? (
                              <a href={offer.preview_url} target="_blank" rel="noopener noreferrer">
                                {offer.preview_url}
                              </a>
                            ) : (
                              <span className="od-text-muted">— Not provided. Click Refresh to re-fetch. —</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="od-general-right">
                        <div className="od-field">
                          <label>Channels</label>
                          <div className="od-value">{deriveChannels()}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {generalTab === "description" && (
                  <div className="od-tab-content od-tab-content-scroll">
                    {!hasAnyDescContent ? (
                      <div className="od-text-muted od-tab-empty">
                        — No description provided. Click Refresh at the top of the page to re-fetch from affiliate API. —
                      </div>
                    ) : (
                      <>
                        {descHtml && (
                          <div className="od-html-content"
                            dangerouslySetInnerHTML={{ __html: descHtml }}/>
                        )}
                        {restrictionsHtml && (
                          <>
                            <h4 className="od-tab-subheading">Restrictions</h4>
                            <div className="od-html-content"
                              dangerouslySetInnerHTML={{ __html: restrictionsHtml }}/>
                          </>
                        )}
                        {termsHtml && (
                          <>
                            <h4 className="od-tab-subheading">Terms and Conditions</h4>
                            <div className="od-html-content"
                              dangerouslySetInnerHTML={{ __html: termsHtml }}/>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Section>

              {hasOptizmo && (
                <Section title="Optizmo Integration">
                  {optizmo?.optizmo_mailer_key && (
                    <div className="od-link-block">
                      <label>Mailer Access Key</label>
                      <div className="od-link-row">
                        <code className="od-link-value">{optizmo.optizmo_mailer_key}</code>
                        <CopyButton text={optizmo.optizmo_mailer_key}/>
                      </div>
                    </div>
                  )}
                  {(optizmo?.tracking_url || unsub?.tracking_url) && (
                    <div className="od-link-block">
                      <label>Optout Link</label>
                      <div className="od-link-row">
                        <code className="od-link-value">{optizmo?.tracking_url || unsub?.tracking_url}</code>
                        <CopyButton text={optizmo?.tracking_url || unsub?.tracking_url}/>
                      </div>
                    </div>
                  )}
                  {suppression?.tracking_url && (
                    <div className="od-link-block">
                      <label>Suppression File</label>
                      <div className="od-link-row">
                        <code className="od-link-value">{suppression.tracking_url}</code>
                        <CopyButton text={suppression.tracking_url}/>
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {(realCreatives.length > 0 || zipBundles.length > 0) && (
                <Section title="Creatives" headerExtra={
                  <span className="od-section-badge">
                    {realCreatives.length} creative{realCreatives.length !== 1 ? "s" : ""}
                  </span>
                }>
                  <div className="od-creative-toolbar">
                    <div className="od-search">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <input type="text" placeholder="Search creatives..."
                        value={creativeSearch}
                        onChange={e => setCreativeSearch(e.target.value)}/>
                      {creativeSearch && (
                        <button onClick={() => setCreativeSearch("")} className="od-search-clear">✕</button>
                      )}
                    </div>
                    <button className="od-download-all-btn" onClick={handleDownloadAll}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download All
                    </button>
                  </div>

                  {realCreatives.length === 0 ? (
                    <div className="od-empty-inline">
                      <p>No individual creatives. Click "Download All" to get the ZIP bundle.</p>
                    </div>
                  ) : (
                    <div className="od-creative-table-wrap">
                      <table className="od-creative-table">
                        <thead>
                          <tr>
                            <th className="th-id">ID</th>
                            <th className="th-name">Name</th>
                            <th className="th-type">Type</th>
                            <th className="th-preview">Preview</th>
                            <th className="th-download">Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCreatives.length === 0 ? (
                            <tr><td colSpan={5} className="od-empty">No creatives match "{creativeSearch}"</td></tr>
                          ) : (
                            filteredCreatives.map(c => {
                              const thumbIsImage = c.creative_url && /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(c.creative_url);
                              return (
                                <tr key={c.id}>
                                  <td className="td-id">{c.creative_external_id || c.id}</td>
                                  <td className="td-name">{c.creative_name || "—"}</td>
                                  <td className="td-type">{typeLabel(c.creative_type)}</td>
                                  <td className="td-preview">
                                    <button
                                      className="od-thumb-btn"
                                      onClick={() => setPreviewCreative(c)}
                                      title="Click to preview">
                                      {thumbIsImage ? (
                                        <img src={c.creative_url} alt="" className="od-thumb"/>
                                      ) : (
                                        <div className="od-thumb od-thumb-empty">
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                          </svg>
                                        </div>
                                      )}
                                    </button>
                                  </td>
                                  <td className="td-download">
                                    <button className="od-dl-btn" onClick={() => handleDownload(c)}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7 10 12 15 17 10"/>
                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                      </svg>
                                      Download
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Section>
              )}
            </div>

            <div className="od-col-right">
              {hasEmail ? (
                <Section title="Email Instructions">
                  {fromLines.length > 0 && (
                    <div className="od-email-block">
                      <div className="od-email-block-head">
                        <label>From Lines <span className="od-count">({fromLines.length})</span></label>
                        <CopyButton text={fromLinesText}/>
                      </div>
                      <EmailLinesBlock items={fromLines}/>
                    </div>
                  )}

                  {fromLines.length > 0 && subjectLines.length > 0 && (
                    <div className="od-email-separator"/>
                  )}

                  {subjectLines.length > 0 && (
                    <div className="od-email-block">
                      <div className="od-email-block-head">
                        <label>Subject Lines <span className="od-count">({subjectLines.length})</span></label>
                        <CopyButton text={subjectLinesText}/>
                      </div>
                      <EmailLinesBlock items={subjectLines}/>
                    </div>
                  )}
                </Section>
              ) : (
                <div className="od-empty-state-card">
                  <div className="od-empty-state-icon">✉️</div>
                  <h3>No Email Instructions</h3>
                  <p>This offer doesn't have any From Lines or Subject Lines defined.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showTrackingModal && (
        <TrackingLinksModal offer={offer} onClose={() => setShowTrackingModal(false)}/>
      )}
      {previewCreative && (
        <CreativePreviewModal
          creative={previewCreative}
          affId={affId}
          offerId={offerId}
          token={token}
          onClose={() => setPreviewCreative(null)}
        />
      )}
    </div>
  );
}
